import React, { useCallback, useEffect, useState } from "react";
import { Box, Divider, Flex, Image, Text, Tooltip } from "@chakra-ui/react";
import AppNavigationLink from "@/features/ui/app-navigation/app-navigation-link";
import type { Location } from "@remix-run/router";
import axios from "axios";
import { useLocation } from "react-router-dom";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Trans } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { exchangeCurrencyStore } from "@/shared/store/exchangeCurrencyStore";
import CurrencySwitch from "@/shared/components/currency-switch";
import { format } from "date-fns";
import ko from "date-fns/locale/ko";
import { enUS } from "date-fns/locale";

export interface LiteralItem {
  pathname: string;
  query?: { [key: string]: string | string[] | null | boolean };
  icon: string;
  children?: LiteralItem[];
  predicates?: ((literal: LiteralItem, location: Location) => boolean)[];
  key: string;
}

export interface Item extends Omit<LiteralItem, "children"> {
  children: Item[];
  isCollapsed: boolean;
  isActive: boolean;
}

// 패스픽 시작 가이드 경로. locale prefix(/ko, /en, /it)를 벗겨 내고 비교한다
// — app-navigation.tsx 의 주문 내역 predicate 와 같은 방식이다.
const PATH_PICK_START_GUIDE_PATHNAME = "/pathpick/start-guide";

export default function AppNavigationBottom() {
  const [currency, setCurrency] = useState<string>("EUR");
  const [currencyCheck, setCurrencyCheck] = useState<boolean>(false);
  const [exchangeRate, setExchangeRate] = useState<string | null>(null);
  const [transferRate, setTransferRate] = useState<string | null>(null);
  const [pathradeTransferRate, setPathradeTransferRate] = useState<
    string | null
  >(null);
  const [rateDt, setRateDt] = useState<string | null>(null);
  const exchangeCurrencyInfo = useRecoilValue(exchangeCurrencyStore);
  const setGlobalExchangeRate = useSetRecoilState(exchangeCurrencyStore);
  const { i18n } = useI18nHelper();
  const identity = useIdentity();
  const location = useLocation();

  useEffect(() => {
    async function getRates() {
      try {
        const res = await axios.get(
          process.env.NEXT_PUBLIC_API_URL + `/exchange-rates`
        );
        setGlobalExchangeRate(res.data);
      } catch (err) {
        console.error("환율 불러오기 실패", err);
      }
    }

    if (!exchangeRate) {
      getRates();
    }
    // ⏰ 5분마다 호출 (5분 = 300000ms)
    const intervalId = setInterval(() => {
      getRates();
    }, 300000); // 5분

    return () => {
      clearInterval(intervalId); // 컴포넌트 언마운트 시 정리
    };
  }, [currency, exchangeRate, setGlobalExchangeRate]);

  useEffect(() => {
    if (!exchangeCurrencyInfo) {
      return;
    }
    setExchangeRate(exchangeCurrencyInfo[currency]?.base_rate);
    setTransferRate(exchangeCurrencyInfo[currency]?.remittance_send_rate);
    setPathradeTransferRate(exchangeCurrencyInfo[currency]?.pathrade_send_rate);
    setRateDt(exchangeCurrencyInfo[currency]?.rate_timestamp);
  }, [currency, exchangeCurrencyInfo]);

  const { t, tTitle, tLocalDateTimeString, tSentence } = useI18nHelper();
  const isKor = i18n.language === "ko";

  const rateDtText = rateDt
    ? (() => {
        const d = new Date(rateDt);
        const locale = isKor ? ko : enUS;
        const pattern = isKor
          ? "M월 d일 a h:mm" // 예: 5월 30일 오후 4:00
          : "MMMM d, h:mm a"; // 예: May 30, 4:00 PM (원하면 MMMM 대신 MMM)
        const s = format(d, pattern, { locale });

        // 영어에서 pm/ am 소문자를 원하면:
        return isKor ? s : s.replace("AM", "am").replace("PM", "pm");
      })()
    : "Unknown";

  let tooltipLabel;

  tooltipLabel = (
    <Box>
      <Trans
        i18nKey="common:exchange_rate_tooltip"
        values={{ rateDt: rateDtText }}
        components={{
          br: <br />,
          b: <Text as="span" fontWeight={700} />,
        }}
      />
    </Box>
  );

  const title = tTitle(`common:exchange_rate_title_${currency}`);
  const [first, second, ...rest] = title.split(" ");
  const newTitle = tSentence("exchange_rate_title");

  return (
    <Flex
      direction="column"
      gap="12px"
      justifyContent={"stretch"}
      paddingTop={"12px"}
    >
      {/* 패스레이드 가이드와 상담 버튼 토글은 BUYER에서만 노출 (채널톡도 BUYER에서만 boot된다). */}
      {identity?.company?.type === "BUYER" && (
        <>
          <AppNavigationLink
            item={{
              key: "pathrade_guide",
              pathname: "",
              icon: "circle_help",
              isCollapsed: false,
              isActive: false,
              children: [],
            }}
            key={`AppNavigationLink_guide`}
            onClick={() => {
              window.open(
                "https://docs.pathrade.com/pathrade-manual",
                "_blank"
              );
            }}
          />
          {/* 패스픽 시작 가이드는 외부 문서가 아니라 앱 안의 페이지라 pathname 을 채워 링크로 동작시킨다.
              패스픽 GNB 메뉴와 달리 멤버십으로 게이트하지 않는다 — 멤버십이 없는 사용자를
              온보딩·무료체험으로 데려오는 것이 이 가이드의 목적이기 때문이다. */}
          <AppNavigationLink
            item={{
              key: "pathpick_start_guide",
              pathname: PATH_PICK_START_GUIDE_PATHNAME,
              icon: "document-text",
              isCollapsed: false,
              isActive:
                location.pathname.replace(/^\/(ko|it|en)/, "") ===
                PATH_PICK_START_GUIDE_PATHNAME,
              children: [],
            }}
            key={`AppNavigationLink_pathPickStartGuide`}
            onClick={() => {
              // pathname 이 있어 AppNavigationLink 가 Link 로 이동시킨다. 별도 동작은 없다.
            }}
          />
        </>
      )}
      <Box
        borderWidth="1px"
        borderRadius="4px"
        padding="12px"
        boxShadow="lg"
        backgroundColor="white"
        width={"180px"}
        zIndex="999"
        marginLeft={"8px"}
        marginBottom={"16px"}
      >
        <Flex gap={"4px"} align="center" mb="8px">
          <Text fontSize="12px" fontWeight={500} lineHeight={"16px"}>
            {newTitle}
          </Text>
          <Tooltip label={tooltipLabel} fontSize="sm">
            <Text as="span" fontSize="sm" color="gray.500" cursor="pointer">
              ⓘ
            </Text>
          </Tooltip>
        </Flex>
        <Flex marginBottom={"12px"}>
          <CurrencySwitch
            checked={currencyCheck}
            onChange={(checked: boolean) => {
              setCurrencyCheck(checked);
              if (checked) {
                setCurrency("USD");
              } else {
                setCurrency("EUR");
              }
            }}
            firstOption={
              <Flex gap={"4px"} alignItems="center">
                <Image
                  src={"/images/EU-flag.png"}
                  alt={"EU 국기"}
                  width={"18px"}
                  height={"12px"}
                />
                <Text fontSize={"14px"} fontWeight={600} lineHeight={"24px"}>
                  {t("currency_EUR")}
                </Text>
              </Flex>
            }
            secondOption={
              <Flex gap={"4px"} alignItems="center">
                <Image
                  src={"/images/US-flag.png"}
                  alt={"US 국기"}
                  width={"18px"}
                  height={"12px"}
                />
                <Text fontSize={"14px"} fontWeight={600} lineHeight={"24px"}>
                  {t("currency_USD")}
                </Text>
              </Flex>
            }
            width={"100%"}
          />
        </Flex>

        <Flex justify="space-between" alignItems="center">
          <Text fontSize="12px" color="#6F6F6C">
            {tSentence("base_rate")}
          </Text>
          <Text fontWeight="bold" fontSize="16px">
            {exchangeRate != null
              ? Number(exchangeRate).toLocaleString("ko-KR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "-"}
          </Text>
        </Flex>

        <Divider my="12px" />

        <Flex justify="space-between">
          <Text fontSize="12px" color="#6F6F6C">
            {tSentence("common:transfer_rate")}
          </Text>
          <Text fontSize="13px" fontWeight={"700px"} color={"#444444"}>
            {transferRate != null
              ? Number(transferRate).toLocaleString("ko-KR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "-"}
          </Text>
        </Flex>

        <Flex justify="space-between" mt="6px">
          <Text fontSize="12px" color="#6F6F6C">
            {tSentence("common:pathrade_transfer_rate")}
          </Text>
          <Text fontSize="13px" fontWeight={"700px"} color={"#444444"}>
            {pathradeTransferRate != null
              ? Number(pathradeTransferRate).toLocaleString("ko-KR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "-"}
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
}
